import { useNavigate, useSearchParams } from 'react-router-dom';
import React from 'react';
import { Spin, Tag } from 'antd';
import BaseTable from '../../../components/BaseTable';
import { useDeleteData, useFetchData, useDeliveryReceiptStore } from '../../../hooks';
import { useAuthStore, useDrawerStore } from '../../../store';
import { PageableResponse } from '../../../types';
import { DeliveryReceiptRecord } from '../../../types/DeliveryReceipt';
import { Resource } from '../../../lib/enums';
import { convertDate, dateFormat } from '../../../lib/utils';

const statusColour: Record<string, string> = {
  DRAFT: 'default',
  CONFIRM: 'processing',
  APPROVED: 'success',
  DONE: 'success',
  CANCEL: 'error',
};

const DeliveryReceiptTable = ({
  api,
  moduleName,
  moduleCode,
}: {
  api: string;
  moduleName: string;
  moduleCode: string;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const drawerStore = useDrawerStore();
  const drStore = useDeliveryReceiptStore();
  const { user } = useAuthStore();

  const userModuleAccess = user?.moduleAccess?.find(
    (item) => item.module.code === moduleCode,
  );

  const { data, isLoading } = useFetchData<PageableResponse<DeliveryReceiptRecord>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.MIN,
      search: searchParams.get('search') || '',
      length: parseInt(searchParams.get('length') || '999999'),
      active: searchParams.get('active') || true,
    },
  );

  const { mutate: deleteRecord } = useDeleteData(api);

  const columns = [
    {
      title: 'DR No.',
      dataIndex: 'code',
      key: 'code',
      render: (code: string, record: DeliveryReceiptRecord) => (
        <a onClick={() => navigate(`/stock-movement/delivery-receipt/${record.id}`)}>
          {code}
        </a>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'requiredDate',
      key: 'requiredDate',
      render: (v: string) => (v ? convertDate(v, dateFormat) : '—'),
    },
    {
      title: 'Delivered To',
      dataIndex: 'destination',
      key: 'destination',
      // Blank until someone says where it is going, which is the one thing a
      // receipt generated from an RR cannot know.
      render: (d: { description?: string } | undefined) =>
        d?.description ?? <span className={'text-amber-600'}>not set</span>,
    },
    { title: 'Source', dataIndex: 'source', key: 'source', render: (s: string) => s || '—' },
    { title: 'Carried By', dataIndex: 'remarks', key: 'remarks', render: (r: string) => r || '—' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag color={statusColour[s] ?? 'default'}>{s}</Tag>,
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <BaseTable<DeliveryReceiptRecord>
        dataSource={data?.data ?? []}
        isLoading={isLoading}
        module={moduleName}
        columns={columns}
        canEdit={userModuleAccess?.write}
        canDelete={userModuleAccess?.delete}
        handleEditButtonClick={(record) => {
          drStore.setData(record);
          drawerStore.onClose();
        }}
        handleDeleteButtonClick={(record) =>
          record.id ? deleteRecord({ id: record.id }) : undefined
        }
      />
    </Spin>
  );
};

export default DeliveryReceiptTable;
